/**
 * Image Downloader - Save pictures
 *
 * Declares a single web-accessible-resource entry whose only glob is `*.*` — every
 * bundled file with a dot in its name — matched to <all_urls> with no `use_dynamic_url`.
 * That exposes the entire package at fixed URLs to any page.
 *
 * The action icon is the cleanest signal: assets/icon.png is vendor-named and, being an
 * image, loads through probeImage with no CORS grant, so it survives a strict page CSP.
 *
 * (Distinct from the separate "Image Downloader" extension already registered — different
 * ID and package.)
 *
 * Manifest verified against version 1.2.9.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'daeljdgmllhgmbdkpgnaojldjkdgkbjg';

export const imageDownloaderSavePictures: ExtensionDefinition = {
  id: ID,
  name: 'Image Downloader - Save pictures',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 300_000,
  notes:
    'One WAR entry globbing *.* to every origin with no dynamic URL, so the whole ' +
    'package is fetchable. The vendor-named icon is conclusive and CORS-free.',
  signals: [
    {
      id: 'war-icon-image',
      kind: 'web-accessible-resource',
      description:
        'Load assets/icon.png via <img> — the extension icon, exposed to every origin ' +
        'and readable without a CORS grant.',
      weight: 1,
      run: () => probeImage(ID, 'assets/icon.png'),
    },
    {
      id: 'war-open-new-tab-image',
      kind: 'web-accessible-resource',
      description:
        'Load assets/open-new-tab.png via <img> — a second bundled image, corroborating ' +
        'without needing a CORS grant.',
      weight: 0.5,
      run: () => probeImage(ID, 'assets/open-new-tab.png'),
    },
    {
      id: 'war-popup-js',
      kind: 'web-accessible-resource',
      description: 'Fetch js/popup.js — the popup script, an independent third path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'js/popup.js'),
    },
  ],
};
